<template>
  <Navigation with-exit @back="send('BACK')" @exit="send('EXIT')" />
  <Title>{{ i18n(translations.title) }}</Title>

  <div class="space-y-12">
    <slot name="before-form" />

    <RevForm :initial-values :validate="validate()" @submit="handleSubmit">
      <template #default="{ values, errors }">
        <div class="space-y-24">
          <RevInputText
            v-if="refundInfo.isPartialRefundAllowed"
            id="refundAmount"
            v-model="values.refundAmount"
            :disabled="isLoading"
            :error="errors.refundAmount"
            :format="
              (amount) => formatAmount(amount, currencyMinimumFractionDigits)
            "
            :label="i18n(translations.inputAmount)"
          />

          <div
            class="flex items-center gap-16"
            :class="[
              { 'mt-12': !refundInfo.isPartialRefundAllowed },
              { 'mt-24': refundInfo.isPartialRefundAllowed },
            ]"
          >
            <slot />
            <RevButton
              :disabled="isLoading"
              :loading="isLoading"
              type="submit"
              variant="primary"
            >
              {{ i18n(translations.refundButton) }}
            </RevButton>
          </div>
        </div>
      </template>
    </RevForm>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'
import { RevButton } from '@ds/components/Button'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInputText } from '@ds/components/InputText'

import type { MachineSend, RefundInfo } from '../../../ActionBar.machine.type'
import Navigation from '../../Navigation/Navigation.vue'
import Title from '../../Title/Title.vue'
import {
  formatAmount,
  isValidNumber,
  numberGreaterThan,
  numberLowerThan,
} from '../RefundFlow.utils'

import translations from './AmountStep.translations'

const i18n = useI18n()
const currentLocale = useI18nLocale()
const logger = useLogger()
const $httpFetch = useHttpFetch()

const { openErrorToast, openSuccessToast } = useTheToast()

const emit = defineEmits(['done', 'save-partial-refund-amount'])

const { customerRequestId, partialRefundAmount, refundInfo, send } =
  defineProps<{
    customerRequestId: number
    partialRefundAmount?: string
    refundInfo: RefundInfo
    send: MachineSend
  }>()

const currencyMinimumFractionDigits = computed(() => {
  const options = Intl.NumberFormat(toBcp47Locale(currentLocale), {
    style: 'currency',
    currency: refundInfo.maxRefundPrice.currency,
  }).resolvedOptions()

  return options.minimumFractionDigits
})

const isLoading = ref(false)

type Values = {
  refundAmount: string
}

const initialValues = reactive<Values>({
  refundAmount: partialRefundAmount || refundInfo.maxRefundPrice.amount,
})

const MIN_REFUNDABLE_AMOUNT = '1'

const validate = () => {
  const minRefundPrice = {
    amount: MIN_REFUNDABLE_AMOUNT,
    currency: refundInfo.maxRefundPrice.currency,
  }

  return makeValidate<Values>({
    refundAmount: [
      required(i18n(translations.isRequired)),
      isValidNumber<Values>(
        i18n(translations.shouldBeLowerThan, {
          maxRefundablePrice: i18n.price(refundInfo.maxRefundPrice),
        }),
      ),
      numberLowerThan<Values>(
        refundInfo.maxRefundPrice.amount,
        i18n(translations.shouldBeLowerThan, {
          maxRefundablePrice: i18n.price(refundInfo.maxRefundPrice),
        }),
      ),
      numberGreaterThan<Values>(
        MIN_REFUNDABLE_AMOUNT,
        i18n(translations.shouldBeGreaterThan, {
          minRefundablePrice: i18n.price(minRefundPrice),
        }),
      ),
    ],
  })
}

const handleSubmit = async (values: Values) => {
  isLoading.value = true

  const { refundAmount } = values
  const formattedAmount = refundAmount.replace(',', '.')
  const isFullRefund =
    parseFloat(formattedAmount) === parseFloat(refundInfo.maxRefundPrice.amount)

  try {
    if (isFullRefund) {
      await $httpFetch('/bm/customer_request/:customerRequestId/refund', {
        method: 'POST',
        pathParams: {
          customerRequestId: customerRequestId.toString(),
        },
        body: {
          monetaryAmount: {
            amount: formattedAmount,
            currency: refundInfo.maxRefundPrice.currency,
          },
        },
      })

      openSuccessToast()

      emit('done')
    } else {
      emit('save-partial-refund-amount', formattedAmount)
      send('PARTIAL_REFUND_INITIATED')
    }
  } catch (err) {
    openErrorToast({
      title: i18n(translations.errorToastTitle),
      content: i18n(translations.errorToastLabel),
    })

    const error = err as Error
    logger.error(error.message, {
      error,
      owners: ['bot-squad-seller-care-front'],
    })
  } finally {
    isLoading.value = false
  }
}
</script>
